import { GridOptions } from 'ag-grid-community';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { CvNumberLocalizePipe } from 'src/app/reusables/pipes/cv-number-localize.pipe';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { GamesService } from 'src/app/gamification/command-center/services/games.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';

@Component({
    selector: 'cv-games-running-modal',
    templateUrl: './games-running-modal.component.html',
    styleUrls: ['./games-running-modal.component.scss']
})
export class GamesRunningModalComponent implements OnInit {
    @ViewChild('gamesRunningModalGrid') agGrid: AgGridAngular;

    gs = null;
    grid: GridOptions;
    isLoading = false;
    gameTypeInfo = [];
    possibleRanges = [];
    selectedRange = 'Day';
    localization = new Map();

    constructor(
        private activeModal: NgbActiveModal,
        private gamesService: GamesService,
        private cvNumberLocalizePipe: CvNumberLocalizePipe,
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private cvConfigurationService: CvConfigurationService
    ) {
        this.gs = this.gamesService.data;
        this.possibleRanges = [
            { key: 'Day', display: this.cvLocalizationService.getCachedText('Controls', 'Today', 'Today') },
            { key: 'Week', display: this.cvLocalizationService.getCachedText('Controls', 'This_Week', 'This Week') },
            { key: 'Month', display: this.cvLocalizationService.getCachedText('Controls', 'This_Month', 'This Month') }
        ];
    }

    ngOnInit(): void {
        this.loadLocalization();
    }

    loadLocalization(): void {
        this.cvLocalizationService.GetSection("Command_Center").then((map) => {
            this.localization = map;
        }).finally(() => {
            this.setupGrid();
            this.buildGamesRunning();
        });
    }

    setupGrid(): void {
        this.grid = {
            defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                cellStyle: {
                    'line-height': '64px',
                },
            },

            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressDragLeaveHidesColumns: true,
            suppressScrollOnNewData: true,
            rowHeight: 64,

            columnDefs: [{
                field: 'Product',
                headerName: this.getColumnName("Game_Type", "Game Type"),
                lockVisible: true,
                cellRenderer: (params) => {
                    var productContainer = `<div class="d-flex flex-row">`;
                    var icon = `<div class="${params.data.GameTypeIcon} my-auto mr-2" style="background-size: contain; min-width: 2.5rem; min-height: 2.5rem"></div>`;
                    productContainer += icon + `<div>${params.value}</div></div>`;

                    return productContainer;
                },
                minWidth: 200
            }, {
                field: 'Running',
                headerName: this.getColumnName("Games_Running", "Games Running"),
                minWidth: 120,
                filter: 'agNumberColumnFilter',
                type: 'numericColumn',
                cellRenderer: (params) => this.cvNumberLocalizePipe.transform(params.value)
            }, {
                field: 'ActivePlayers',
                headerName: this.getColumnName("Active_Players", "Active Players"),
                minWidth: 120,
                filter: 'agNumberColumnFilter',
                type: 'numericColumn',
                cellRenderer: (params) => this.cvNumberLocalizePipe.transform(params.value)
            }, {
                field: 'EligiblePlayers',
                headerName: this.getColumnName("Eligible_Players", "Eligible Players"),
                minWidth: 135,
                filter: 'agNumberColumnFilter',
                type: 'numericColumn',
                cellRenderer: (params) => this.cvNumberLocalizePipe.transform(params.value)
            }],

            rowData: null,
            getLocaleText: this.agGridHelperService.getLocaleText,

            getRowId: ({ data: rowData }) => rowData.GameType,

            onGridReady: (e) => {
                e.api.sizeColumnsToFit();
            }
        };
    }

    buildGamesRunning(): void {
        // load if necessary
        if (this.gs.thisMonthParticipants == null) {
            this.isLoading = true;
            this.gamesService.loadParticipants().then(() => {
                if (this.gs.thisMonthParticipants != null) {
                    this.buildGamesRunning();
                }
                this.isLoading = false;
            });
            return;
        }

        // filter games
        let gamesByID = {};
        let gameTypes = {};

        this.gs.activeGames.forEach((game) => {
            gamesByID[game.underlyingRule.RuleID] = game;
            if (gameTypes[game.underlyingRule.GameType] == undefined) {
                gameTypes[game.underlyingRule.GameType] = {
                    Name: game.gameTypeName,
                    Icon: game.iconClass,
                    Count: 0,
                    GamePeriods: {
                        Month: {
                            Participants: 0,
                            Eligible: 0,
                        },
                        Week: {
                            Participants: 0,
                            Eligible: 0,
                        },
                        Day: {
                            Participants: 0,
                            Eligible: 0,
                        },
                    }
                };
            }
            gameTypes[game.underlyingRule.GameType].Count++;
        });

        // note: most of these date functions are part of datejs
        let today = Date.today();
        let tomorrow = today.clone().addDays(1);
        let isToday = (date) => {
            return today <= date && date < tomorrow;
        };

        let dayOfWeek = today.getDay();
        let weekStart = dayOfWeek > this.cvConfigurationService.weekStart ? today.clone().addDays(this.cvConfigurationService.weekStart - dayOfWeek) : today.clone().addDays(dayOfWeek - this.cvConfigurationService.weekStart * -1);
        let nextWeekStart = weekStart.clone().addDays(7);
        let isThisWeek = (date) => {
            return weekStart <= date && date < nextWeekStart;
        }

        let monthStart = today.clone().addDays(today.getDate() * -1 + 1);
        let nextMonthStart = monthStart.clone().addMonths(1);
        let isThisMonth = (date) => {
            return monthStart <= date && date < nextMonthStart;
        }

        // count participants
        gamesByID = this.gs.thisMonthParticipants.reduce((perGame, participant) => {
            let gameID = participant.RuleID;
            let period = participant.PeriodStartDate;
            if (perGame[gameID] == undefined) {
                return perGame; // game not active or not loaded
            }

            let gameType = perGame[gameID].underlyingRule.GameType;

            if (perGame[gameID].GamePeriods == undefined) {
                perGame[gameID].GamePeriods = {
                    Month: {
                        Participants: 0,
                        Eligible: 0,
                    },
                    Week: {
                        Participants: 0,
                        Eligible: 0,
                    },
                    Day: {
                        Participants: 0,
                        Eligible: 0,
                    },
                };
            }

            if (gameTypes[gameType].GamePeriods[period] == undefined) {
                gameTypes[gameType].GamePeriods[period] = {
                    Participants: 0,
                    Eligible: 0,
                };
            }

            if (perGame[gameID].GamePeriods[period] == undefined) {
                perGame[gameID].GamePeriods[period] = {
                    Participants: 0,
                    Eligible: 0,
                };
            }

            let isParticipant = participant.DateJoined != null;
            if (isParticipant) {
                perGame[gameID].GamePeriods[period].Participants++;
                gameTypes[gameType].GamePeriods[period].Participants++;
            }
            perGame[gameID].GamePeriods[period].Eligible++;
            gameTypes[gameType].GamePeriods[period].Eligible++;

            let date = new Date(period);
            if (isToday(date)) {
                if (isParticipant) {
                    perGame[gameID].GamePeriods.Day.Participants++;
                    gameTypes[gameType].GamePeriods.Day.Participants++;
                }
                perGame[gameID].GamePeriods.Day.Eligible++;
                gameTypes[gameType].GamePeriods.Day.Eligible++;
            }
            if (isThisWeek(date)) {
                if (isParticipant) {
                    perGame[gameID].GamePeriods.Week.Participants++;
                    gameTypes[gameType].GamePeriods.Week.Participants++;
                }
                perGame[gameID].GamePeriods.Week.Eligible++;
                gameTypes[gameType].GamePeriods.Week.Eligible++;
            }
            if (isThisMonth(date)) {
                if (isParticipant) {
                    perGame[gameID].GamePeriods.Month.Participants++;
                    gameTypes[gameType].GamePeriods.Month.Participants++;
                }
                perGame[gameID].GamePeriods.Month.Eligible++;
                gameTypes[gameType].GamePeriods.Month.Eligible++;
            }

            return perGame;
        }, gamesByID);

        this.gameTypeInfo = [];
        for (var gameType in gameTypes) {
            let info = gameTypes[gameType];
            this.gameTypeInfo.push({
                GameType: gameType,
                GameTypeIcon: info.Icon,
                Product: info.Name,
                Running: info.Count,
                ActivePlayers: info.GamePeriods[this.selectedRange].Participants,
                EligiblePlayers: info.GamePeriods[this.selectedRange].Eligible,
            });
        }

        if (this.agGrid?.api) {
            this.agGrid?.api.setGridOption('rowData', this.gameTypeInfo);
        }
    }

    getColumnName(key: string, defaultValue: string): string {
        if (this.localization && this.localization.has(key))
            return this.localization.get(key);
        else
            return defaultValue;
    }

    closeModal(): void {
        this.activeModal.close();
    }
}